import { useQuery } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import { Token, TokenResponse, TokenErrorResponse } from 'src/features/auth/types';
import { formatResourcePath } from 'src/utils/regex';

import { USER_QUERY_KEY } from 'src/features/auth/utils/constants';
import axios from 'src/lib/axios';
import { QueryConfig } from 'src/lib/react-query';
import { storage } from 'src/utils/storage';

const RESOURCE = '/users/token-refresh/';

type DTO = {
    refresh: string;
};

type ActionFnType = (data: DTO) => Token | null;
const queryFn = (data: DTO): Promise<TokenResponse | TokenErrorResponse> => {
    return axios.post(formatResourcePath(RESOURCE), data);
};

// We simply want to return a user if the token is still valid. Otherwise, we want to refresh the token.
interface UseQueryOptions {
    options?: QueryConfig<ActionFnType>;
}
const useQueryHook = ({ options }: UseQueryOptions = {}) =>
    useQuery({
        queryKey: [USER_QUERY_KEY],
        queryFn: async () => {
            const { refresh, access } = storage.getToken();
            if (access == null || refresh == null) return null;

            const decodedAccessToken = jwtDecode<Token>(access);
            const accessTokenExpirySeconds = decodedAccessToken.exp;
            const currentSeconds = Math.floor(new Date().getTime() / 1000);

            if (accessTokenExpirySeconds < currentSeconds) {
                const response = await queryFn({ refresh });
                if ('code' in response && response.code === 'token_not_valid') {
                    storage.clearToken();
                    return null;
                }
                const { access } = response as TokenResponse;
                storage.setToken({ access });
                return jwtDecode<Token>(access);
            }

            return decodedAccessToken;
        },
        ...options,
    });

export { queryFn as getAccessToken, useQueryHook as useToken };
